import { useEffect, useRef } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ACTIVITY_CLASSIFICATION_OPTIONS } from '../lib/constants';
import { tagOptionsFor } from '../lib/categoryRules';

const NOT_SPECIFIED = '__none';

// Tag and Activity Classification, restricted by the selected category (see CATEGORY_RULES in the backend):
// the Activity Classification list is cut down to what the category allows (and chosen automatically when there is
// only one), and the Tag is disabled, a restricted dropdown, or - for categories without a rule - free text.
export function TagAndActivityFields({ control, errors, setValue, getValues, rules, renderFreeTag }) {
  const category = useWatch({ control, name: 'category' });
  const activityClassification = useWatch({ control, name: 'activity_classification' });

  const allowedClassifications = rules[category]?.activity_classifications;
  const tagRule = tagOptionsFor(rules, category, activityClassification);
  const tagRuleKey = JSON.stringify(tagRule ?? null) + (tagRule === undefined ? ':free' : '');

  // A classification the category doesn't allow is replaced (or cleared when there is a choice to make)
  useEffect(() => {
    if (!allowedClassifications) return;
    if (!allowedClassifications.includes(getValues('activity_classification'))) {
      setValue('activity_classification', allowedClassifications.length === 1 ? allowedClassifications[0] : '');
    }
  }, [category, allowedClassifications]); // eslint-disable-line react-hooks/exhaustive-deps

  // A tag the rule doesn't allow is cleared
  useEffect(() => {
    const current = getValues('tag') || '';
    if ((tagRule === null && current) || (Array.isArray(tagRule) && current && !tagRule.includes(current))) {
      setValue('tag', '');
    }
  }, [tagRuleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const classifications = allowedClassifications || ACTIVITY_CLASSIFICATION_OPTIONS;

  return (
    <>
      <div className="space-y-2">
        <Label>
          Tag
          {Array.isArray(tagRule) && <span className="text-destructive ml-1">*</span>}
        </Label>
        <Controller
          name="tag"
          control={control}
          render={({ field }) => {
            if (tagRule === null) {
              return <Input value="" disabled placeholder="Not used for this category" className="input-eco bg-gray-100" data-testid="input-tag" />;
            }
            if (Array.isArray(tagRule)) {
              return (
                <Select onValueChange={field.onChange} value={field.value || ''}>
                  <SelectTrigger className={`input-eco ${errors.tag ? 'border-red-500 ring-1 ring-red-500' : ''}`} data-testid="select-tag">
                    <SelectValue placeholder="Select tag" />
                  </SelectTrigger>
                  <SelectContent>
                    {tagRule.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                  </SelectContent>
                </Select>
              );
            }
            return renderFreeTag(field);
          }}
        />
        {tagRule === null && (
          <p className="text-xs text-muted-foreground">
            {category && rules[category]?.tags
              ? 'Tag is only used for some activity classifications of this category'
              : 'Tag is not used for this category'}
          </p>
        )}
        {errors.tag && <p className="text-sm text-destructive">{errors.tag.message}</p>}
      </div>

      <div className="space-y-2">
        <Label>Activity Classification <span className="text-destructive">*</span></Label>
        <Controller
          name="activity_classification"
          control={control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value || ''}>
              <SelectTrigger
                className={`input-eco ${errors.activity_classification ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                data-testid="select-activity-classification"
              >
                <SelectValue placeholder="Select activity classification" />
              </SelectTrigger>
              <SelectContent>
                {classifications.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        />
        {errors.activity_classification && <p className="text-sm text-destructive">{errors.activity_classification.message}</p>}
      </div>
    </>
  );
}

// Continent (optional). A country in the country_continent table fills it in and locks it; for "*" (any country) or a
// country that is not in the table the continent is picked by hand.
export function ContinentField({ control, setValue, continents, countryContinents }) {
  const country = useWatch({ control, name: 'country' });
  const mapped = countryContinents[country];
  const previousMapped = useRef(null);

  useEffect(() => {
    if (mapped) {
      setValue('continent', mapped);
    } else if (previousMapped.current) {
      setValue('continent', ''); // moved away from a country that filled it in: a stale value must not stay
    }
    previousMapped.current = mapped || null;
  }, [mapped]); // eslint-disable-line react-hooks/exhaustive-deps

  const hint = mapped
    ? 'Filled in from the selected country'
    : country === '*'
      ? 'Any country: select the continent if this factor applies to one'
      : country
        ? 'This country is not in the continent list: select it if needed'
        : 'Filled in from the country, or select it when the country is * (any)';

  return (
    <div className="space-y-2">
      <Label>Continent <span className="text-muted-foreground text-xs">(optional)</span></Label>
      <Controller
        name="continent"
        control={control}
        render={({ field }) => (
          <Select onValueChange={(v) => field.onChange(v === NOT_SPECIFIED ? '' : v)} value={field.value || NOT_SPECIFIED} disabled={!!mapped}>
            <SelectTrigger className={`input-eco ${mapped ? 'bg-gray-100' : ''}`} data-testid="select-continent">
              <SelectValue placeholder="Select continent" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NOT_SPECIFIED}>Not specified</SelectItem>
              {continents.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
